import React from 'react';
import {Card, Row, Col, Tag, Typography, Space, Button, Spin, Alert} from 'antd';
import {
    UserOutlined,
    ApartmentOutlined,
    FileTextOutlined,
    RiseOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import {useGetAdminAnalyticsQuery, useGetAdminVersionQuery} from '../../services/nestjs-base-api';
import {useAuth} from '../../hooks/useAuth';
import { getAppVersion } from '../../utils/version';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { EVENT_TYPE_COLORS, formatEventType } from '../utils/eventTypeUtils';
import { AITokenUsageWidget } from '../components/AITokenUsageWidget';
import { StatisticWithMock } from '../components/StatisticWithMock';

const {Title, Text} = Typography;

export const AdminDashboard: React.FC = () => {
    const {user} = useAuth();
    const {data: analytics, isLoading, error} = useGetAdminAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
    });
    const {data: version} = useGetAdminVersionQuery({
        jwtToken: user?.jwtToken || '',
    });

    // Get app version using utility that handles dev/production scenarios
    const appVersion = getAppVersion();

    // Survey version is now included in the API version response
    const surveyVersion = version?.surveyVersion;
    const websiteVersion = version?.websiteVersion;
    
    if (isLoading) {
        return (
            <div style={{padding: '24px', textAlign: 'center'}}>
                <Spin size="large" />
            </div>
        );
    }
    
    if (error) {
        return (
            <div style={{padding: '24px'}}>
                <Alert
                    message="Error loading dashboard data"
                    description="Unable to fetch platform analytics. Please try again later."
                    type="error"
                    showIcon
                />
            </div>
        );
    }
    
    const metrics = analytics || {
        totalOrganisations: 0,
        activeOrganisations: 0,
        totalUsers: 0,
        totalSurveys: 0,
        activeSurveys: 0,
        totalResponses: 0,
        responsesLast30Days: 0,
        averageResponsesPerSurvey: 0,
        organizationGrowthRate: 0,
        topOrganisations: [],
        recentOrganizations: [],
        recentEvents: [],
        showMockData: false,
        mockOrganisations: 0,
        mockActiveOrganisations: 0,
        mockUsers: 0,
        mockSurveys: 0,
        mockActiveSurveys: 0,
        mockResponses: 0,
        mockResponsesLast30Days: 0,
    };
    
    const recentOrganisations = analytics?.recentOrganizations || [];
    const recentEvents = analytics?.recentEvents || [];

    const organisationColumns = [
        {
            title: 'Organisation',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => (
                <Link to={`/admin/organizations/${record.id}`}>
                    <Text strong className="text-blue-600 hover:text-blue-800">{text}</Text>
                </Link>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
                    {status?.toUpperCase() || 'UNKNOWN'}
                </Tag>
            ),
        },
        {
            title: 'Users',
            dataIndex: 'userCount',
            key: 'userCount',
        },
        {
            title: 'Surveys',
            dataIndex: 'surveyCount',
            key: 'surveyCount',
        },
        {
            title: 'Responses',
            dataIndex: 'responseCount',
            key: 'responseCount',
        },
        {
            title: 'Last Activity',
            dataIndex: 'lastActivityAt',
            key: 'lastActivityAt',
            render: (date: string) => date ? new Date(date).toLocaleDateString() : 'N/A',
        },
        {
            title: 'Action',
            key: 'action',
            render: (_: any, record: any) => (
                <Link to={`/admin/organizations/${record.id}`}>
                    <Button type="link" size="small" icon={<EyeOutlined />}>
                        View
                    </Button>
                </Link>
            ),
        },
    ];

    const eventColumns = [
        {
            title: 'Time',
            dataIndex: 'time',
            key: 'time',
            render: (time: string) => time ? new Date(time).toLocaleString() : 'N/A',
        },
        {
            title: 'Organisation',
            dataIndex: 'organization',
            key: 'organization',
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: string) => (
                <Tag color={EVENT_TYPE_COLORS[type] || 'default'}>
                    {formatEventType(type)}
                </Tag>
            ),
        },
    ];

    return (
        <div style={{padding: '24px'}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>
                    Platform Overview
                </Title>
                <div style={{ textAlign: 'right' }}>
                    <Space direction="vertical" size="small" style={{ alignItems: 'flex-end' }}>
                        <div>
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                App Version
                            </Text>
                            <Text strong style={{ fontSize: '14px' }}>
                                v{appVersion}
                            </Text>
                        </div>
                        {version && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                    API Version
                                </Text>
                                <Text strong style={{ fontSize: '14px' }}>
                                    v{version.version}
                                </Text>
                            </div>
                        )}
                        {surveyVersion && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                    Survey Version
                                </Text>
                                <Text strong style={{ fontSize: '14px' }}>
                                    v{surveyVersion.version}
                                </Text>
                            </div>
                        )}
                        {websiteVersion && (
                            <div>
                                <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                    Website Version
                                </Text>
                                <Text strong style={{ fontSize: '14px' }}>
                                    v{websiteVersion.version}
                                </Text>
                            </div>
                        )}
                    </Space>
                </div>
            </div>

            {/* Metrics Cards */}
            <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Organisations"
                            realValue={metrics.totalOrganisations}
                            mockValue={metrics.mockOrganisations}
                            prefix={<ApartmentOutlined />}
                            showMockData={metrics.showMockData}
                            footer={
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ color: '#52c41a' }}>
                                        {metrics.activeOrganisations} active
                                    </Text>
                                    {metrics.showMockData && metrics.mockActiveOrganisations && metrics.mockActiveOrganisations > 0 && (
                                        <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                                            +{metrics.mockActiveOrganisations} mock active
                                        </Text>
                                    )}
                                </Space>
                            }
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Users"
                            realValue={metrics.totalUsers}
                            mockValue={metrics.mockUsers}
                            prefix={<UserOutlined />}
                            showMockData={metrics.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Surveys"
                            realValue={metrics.totalSurveys}
                            mockValue={metrics.mockSurveys}
                            prefix={<FileTextOutlined />}
                            showMockData={metrics.showMockData}
                            footer={
                                metrics.showMockData && metrics.mockActiveSurveys && metrics.mockActiveSurveys > 0 && (
                                    <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                                        {metrics.activeSurveys} real active, +{metrics.mockActiveSurveys} mock active
                                    </Text>
                                )
                            }
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Responses"
                            realValue={metrics.totalResponses}
                            mockValue={metrics.mockResponses}
                            prefix={<RiseOutlined />}
                            showMockData={metrics.showMockData}
                            footer={
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" style={{ color: '#52c41a' }}>
                                        {metrics.responsesLast30Days} last 30 days
                                    </Text>
                                    {metrics.showMockData && metrics.mockResponsesLast30Days && metrics.mockResponsesLast30Days > 0 && (
                                        <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                                            +{metrics.mockResponsesLast30Days} mock last 30 days
                                        </Text>
                                    )}
                                </Space>
                            }
                        />
                    </Card>
                </Col>
            </Row>

            {/* AI Token Usage Widget */}
            <AITokenUsageWidget />

            {/* Recent Activity */}
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={14}>
                    <Card
                        title="Recent Organisations"
                        extra={<Link to="/admin/organizations">View All</Link>}
                    >
                        <ResponsiveTable
                            dataSource={recentOrganisations}
                            columns={organisationColumns}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card
                        title="Recent Events"
                        extra={<Link to="/admin/events">View All</Link>}
                    >
                        <ResponsiveTable
                            dataSource={recentEvents}
                            columns={eventColumns}
                            pagination={false}
                            size="small"
                            showHeader={false}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Quick Actions */}
            <Card title="Quick Actions" style={{marginTop: 16}}>
                <Space wrap>
                    <Button type="primary">
                        <Link to="/admin/users">Manage Platform Users</Link>
                    </Button>
                    <Button>
                        <Link to="/admin/organizations">View Organisations</Link>
                    </Button>
                    <Button>
                        <Link to="/admin/trends">View Trends</Link>
                    </Button>
                    <Button>
                        <Link to="/admin/events">View Event Log</Link>
                    </Button>
                    <Button>
                        <Link to="/admin/system-settings">System Settings</Link>
                    </Button>
                </Space>
            </Card>
        </div>
    );
};